import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {Dialog, Drawer, Menu, MenuItem, useMediaQuery} from '@mui/material';
import jwtInstance from '../../../guards/jwt.interceptor';
import {errorResponse, successResponse} from '../../../utils/getResponse';
import {ALL, allOption, AVATAR_COLORS, COSTS} from './tmData';

// ---------- Context: modale + utilizatorul logat ----------

export interface TmContextValue {
	open: (type: string, data?: any) => void;
	close: () => void;
	me: any;
	reload: () => void;
	version: number;
}

export const TmContext = createContext<TmContextValue>({
	open: () => {}, close: () => {}, me: null, reload: () => {}, version: 0,
});
export const useTm = () => useContext(TmContext);

// ---------- API ----------

export const tmError = (error: any) => errorResponse(error, 'Transfer market', 'danger');
export const tmSuccess = (response: any) => successResponse(response, 'Transfer market', 'success');

// Parametrii „Toate” nu se trimit la server
export const cleanParams = (params: Record<string, any>) => Object.fromEntries(
	Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== ALL),
);

export const tmGet = (url: string, params?: Record<string, any>) => jwtInstance.get(url, {params: params ? cleanParams(params) : undefined});

// ---------- Formatari ----------

export const initials = (name?: string) => (name || '')
	.split(/[\s-]+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

export const colorFor = (seed?: string | number) => {
	const s = String(seed ?? '');
	let h = 0;
	for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
	return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const DAY = 24 * 60 * 60 * 1000;

export const daysAgo = (date?: string) => {
	if (!date) return null;
	const d = new Date(date);
	const start = new Date(); start.setHours(0, 0, 0, 0);
	const that = new Date(d); that.setHours(0, 0, 0, 0);
	return Math.round((start.getTime() - that.getTime()) / DAY);
};

// „acum 3 zile”, „ieri”, „azi”
export const timeAgo = (date?: string) => {
	const days = daysAgo(date);
	if (days === null) return '';
	if (days <= 0) return 'azi';
	if (days === 1) return 'ieri';
	if (days < 20) return `acum ${days} zile`;
	if (days < 60) return `acum ${days} de zile`;
	return `acum ${Math.round(days / 30)} luni`;
};

export const daysLeft = (date?: string) => {
	if (!date) return null;
	return Math.max(0, Math.round((new Date(date).getTime() - Date.now()) / DAY));
};

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const costLabel = (cost?: string) => COSTS.find(c => c.value === cost)?.label ?? cost ?? '';

export const fullName = (p: any) => p ? `${p.lastName ?? ''} ${p.firstName ?? ''}`.trim() : '';

export const waHref = (phone?: string, text?: string) => {
	const digits = (phone || '').replace(/\D/g, '').replace(/^0/, '40');
	return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
};

export const shareHref = (text: string) => `https://wa.me/?text=${encodeURIComponent(text)}`;

export const isLeagueOb = (competition?: string) => (competition || '').toLowerCase().includes('old');

// ---------- Iconite (preluate din design) ----------

const Svg = ({size = 18, children, ...rest}: any) => (
	<svg width={size} height={size} viewBox='0 0 24 24' aria-hidden='true' {...rest}>{children}</svg>
);

export const IconWhatsApp = ({size = 20, color = 'currentColor', style}: any) => (
	<Svg size={size} fill={color} style={style}>
		<path d='M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.3-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 12 12 0 0 0 4.6 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.5-.3z'/>
	</Svg>
);

export const IconPhone = ({size = 18}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='2.2'>
		<path d='M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z'/>
	</Svg>
);

export const IconChevron = () => (
	<Svg size={14} fill='none' stroke='currentColor' strokeWidth='2.5'><path d='m6 9 6 6 6-6'/></Svg>
);

export const IconPlus = ({size = 26}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='3'><path d='M12 5v14M5 12h14'/></Svg>
);

export const IconCamera = ({size = 26, strokeWidth = 2}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth={strokeWidth}>
		<rect x='3' y='5' width='18' height='14' rx='2'/>
		<circle cx='12' cy='12' r='3.5'/>
		{strokeWidth === 2 && <path d='M8 5l1.5-2h5L16 5'/>}
	</Svg>
);

export const IconUpload = () => (
	<Svg size={18} fill='none' stroke='currentColor' strokeWidth='2.2'><path d='M12 16V4M7 9l5-5 5 5'/><path d='M4 20h16'/></Svg>
);

export const IconSearch = ({size = 18}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='2.2'><circle cx='11' cy='11' r='7'/><path d='m20 20-3.5-3.5'/></Svg>
);

// Cautarea din Transferuri → Jucatori liberi (SearchField), in stilul vtm: trimite textul dupa 400 ms de pauza
export const TmSearch = ({placeholder, onSearch}: { placeholder: string; onSearch: (value: string) => void }) => {
	const [value, setValue] = useState('');
	useEffect(() => {
		const id = setTimeout(() => onSearch(value.trim()), 400);
		return () => clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);
	return (
		<div className='vtm-input-wrap vtm-search'>
			<IconSearch/>
			<input className='vtm-input vtm-input--wa' type='search' value={value} onChange={e => setValue(e.target.value)}
				   placeholder={placeholder} aria-label={placeholder}/>
		</div>
	);
};

export const IconPlay = ({size = 12}: any) => (
	<Svg size={size} fill='currentColor'><path d='M7 4v16l13-8z'/></Svg>
);

export const IconCheck = ({size = 34}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='3'><path d='m5 12 5 5L20 7'/></Svg>
);

export const IconUser = ({size = 40}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='1.8'>
		<circle cx='12' cy='8' r='4'/><path d='M4 21c0-4 3.6-7 8-7s8 3 8 7'/>
	</Svg>
);

// ---------- Atomi ----------

export const Pos = ({children, age, variant, className = ''}: any) => (
	<span className={`vtm-pos${variant ? ` vtm-pos--${variant}` : ''} ${className}`.trim()}>
		{children}{age && <em>{age}</em>}
	</span>
);

export const Cost = ({cost, label, className = ''}: any) => (
	<span className={`vtm-cost${cost === 'free' ? ' vtm-cost--free' : ''} ${className}`.trim()}>{label ?? costLabel(cost)}</span>
);

export const Liga = ({competition, children}: any) => (
	<span className={`vtm-liga${isLeagueOb(competition) ? ' vtm-liga--ob' : ''}`}>{children}</span>
);

export const Avatar = ({name, seed, photo, size, free, className = '', children, label}: any) => {
	if (free) return <div className={`vtm-av${size ? ` vtm-av--${size}` : ''} vtm-av--free ${className}`.trim()}>?</div>;
	return (
		<div className={`vtm-av${size ? ` vtm-av--${size}` : ''} ${className}`.trim()}
			 style={{background: colorFor(seed ?? name), ...(photo ? {overflow: 'hidden'} : {})}}>
			{photo ? <img src={photo} alt={name} style={{width: '100%', height: '100%', objectFit: 'cover'}}/> : (label ?? initials(name))}
			{children}
		</div>
	);
};

export const AvatarStack = ({members, count, slots}: any) => {
	const shown = (members || []).slice(0, 4);
	const rest = count - shown.length;
	const free = Math.max(0, Math.min(slots, 2));
	return (
		<div className='vtm-av-stack'>
			{shown.map((m: any) => <Avatar key={m.uuid} name={m.name} seed={m.uuid}/>)}
			{rest > 0 && <Avatar label={`+${rest}`} seed='rest'/>}
			{Array.from({length: free}).map((_, i) => <Avatar key={`f${i}`} free/>)}
		</div>
	);
};

export const Progress = ({count, min, color, dark}: any) => (
	<div className={`vtm-bar${dark ? ' vtm-bar--dark' : ''}`}>
		<i style={{width: `${Math.min(100, Math.round(count / min * 100))}%`, ...(color ? {background: color} : {})}}/>
	</div>
);

export const TeamLogo = ({team, size}: any) => (
	<div className={`vtm-logo${size ? ` vtm-logo--${size}` : ''}`} aria-hidden='true'
		 style={team?.logo ? {background: '#fff', overflow: 'hidden'} : {background: colorFor(team?.name)}}>
		{team?.logo ? <img src={team.logo} alt='' style={{width: '100%', height: '100%', objectFit: 'contain'}}/> : initials(team?.name)}
	</div>
);

// Chip de filtru cu meniu (Competiție · Toate ˅)
export const FilterChip = ({label, value, options, onChange, includeAll = true, variant, chevron = true}: any) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const list = includeAll ? [allOption, ...options] : options;
	const current = list.find((o: any) => o.value === value) ?? list[0];

	return (
		<>
			<button type='button' className={`vtm-chip${variant ? ` vtm-chip--${variant}` : ''}`}
					aria-haspopup='listbox' aria-expanded={Boolean(anchor)}
					onClick={(e) => setAnchor(e.currentTarget)}>
				<span className='vtm-chip-k'>{label}</span> {current.label}
				{chevron && <IconChevron/>}
			</button>
			<Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} className='vtm-menu'>
				{list.map((o: any) => (
					<MenuItem key={o.value} selected={o.value === current.value}
							  onClick={() => {
								  onChange(o.value);
								  setAnchor(null);
							  }}>
						{o.label}
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export const IconFilters = ({size = 18}: any) => (
	<Svg size={size} fill='none' stroke='currentColor' strokeWidth='2.2'><path d='M4 6h16M7 12h10M10 18h4'/></Svg>
);

export interface TmFilter {
	key: string;
	label: string;
	value: string;
	options: {value: string, label: string, count?: number}[];
	onChange: (value: string) => void;
	includeAll?: boolean;
}

export const activeFilters = (filters: TmFilter[]) => filters.filter(f => f.value !== ALL).length;

// Butonul „Filtre” din bara tab-ului: numarul de filtre active pe pastila
export const FiltersButton = ({count, onClick}: {count: number, onClick: () => void}) => (
	<button type='button' className={`vtm-chip${count ? ' vtm-chip--on' : ''}`} aria-haspopup='dialog' onClick={onClick}>
		<IconFilters/> Filtre
		{count > 0 && <span className='vtm-chip-n' aria-label={`${count} active`}>{count}</span>}
	</button>
);

// Filtrele unui tab intr-un drawer: din dreapta pe desktop, de jos pe telefon. Se aplica pe loc;
// butonul de jos inchide drawer-ul si arata cate rezultate ies.
export const TmFilterDrawer = ({open, onClose, filters, onReset, resultLabel}: {
	open: boolean, onClose: () => void, filters: TmFilter[], onReset: () => void, resultLabel: string,
}) => {
	const mobile = useMediaQuery('(max-width:767px)');
	const count = activeFilters(filters);
	return (
		<Drawer anchor={mobile ? 'bottom' : 'right'} open={open} onClose={onClose}
				slotProps={{backdrop: {sx: {bgcolor: 'rgba(20, 20, 24, .55)'}}}}
				PaperProps={{className: `vtm-modal vtm-drawer${mobile ? ' vtm-drawer--bottom' : ''}`, role: 'dialog', 'aria-labelledby': 'vtm-filters-title'} as any}>
			{mobile && <div className='vtm-drawer-grip' aria-hidden='true'/>}
			<div className='vtm-drawer-head'>
				<div>
					<span className='vtm-lbl'>{count ? `${count} ${count === 1 ? 'filtru activ' : 'filtre active'}` : 'Fără filtre'}</span>
					<h2 id='vtm-filters-title' className='vtm-ki vtm-drawer-t'>Filtre</h2>
				</div>
				<CloseButton onClose={onClose}/>
			</div>
			<div className='vtm-drawer-body'>
				{filters.map(f => {
					const list: TmFilter['options'] = f.includeAll === false ? f.options : [allOption, ...f.options];
					return (
						<div key={f.key} className='vtm-drawer-group' role='radiogroup' aria-label={f.label}>
							<div className='vtm-lbl'>{f.label}</div>
							<div className='vtm-lvl'>
								{list.map(o => {
									const on = o.value === f.value;
									return (
										<button key={o.value} type='button' role='radio' aria-checked={on} className={on ? 'is-on' : ''}
												onClick={() => f.onChange(o.value)}>
											{o.label}
											{o.count !== undefined && <span className='vtm-lvl-n'>{o.count.toLocaleString('ro-RO')}</span>}
										</button>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
			<div className='vtm-drawer-foot'>
				<button type='button' className='vtm-btn vtm-btn--s' onClick={onReset} disabled={!count}>Resetează</button>
				<button type='button' className='vtm-btn vtm-btn--p' onClick={onClose}>{resultLabel}</button>
			</div>
		</Drawer>
	);
};

// Grup de optiuni tip pastila; multiple=true permite mai multe selectii
export const Choice = ({options, value, onChange, multiple, small}: any) => {
	const isOn = (o: string) => (multiple ? value.includes(o) : value === o);
	const toggle = (o: string) => {
		if (!multiple) return onChange(o);
		onChange(value.includes(o) ? value.filter((v: string) => v !== o) : [...value, o]);
	};
	return (
		<div className='vtm-lvl' role={multiple ? 'group' : 'radiogroup'}>
			{options.map((o: string) => (
				<button key={o} type='button' className={isOn(o) ? 'is-on' : ''} aria-pressed={isOn(o)}
						style={small ? {minHeight: 34, padding: '6px 10px', fontSize: 12} : undefined}
						onClick={() => toggle(o)}>
					{o}
				</button>
			))}
		</div>
	);
};

// ---------- Modal ----------

export const TmModal = ({open = true, onClose, size, children, labelledBy}: any) => {
	const mobile = useMediaQuery('(max-width:600px)');
	return (
		<Dialog open={open} onClose={onClose} maxWidth={false} scroll='body' aria-labelledby={labelledBy}
				slotProps={{backdrop: {sx: {bgcolor: 'rgba(20, 20, 24, .72)'}}}}
				PaperProps={{
					className: `vtm-modal${size ? ` vtm-modal--${size}` : ''}`,
					sx: {m: mobile ? 1 : 4, width: mobile ? 'calc(100% - 16px)' : '100%'},
				}}>
			{children}
		</Dialog>
	);
};

export const CloseButton = ({onClose, className = ''}: any) => (
	<button type='button' className={`vtm-btn vtm-btn--s vtm-btn--close ${className}`.trim()} aria-label='Închide'
			onClick={onClose}>✕</button>
);

export const ModalHead = ({label, onClose, children}: {label?: string, onClose: () => void, children?: ReactNode}) => (
	<div className='vtm-modal-head'>
		{children ?? <span className='vtm-lbl'>{label}</span>}
		<CloseButton onClose={onClose}/>
	</div>
);

// Indicatorul de pasi al formularelor; cu onPick pasii sunt butoane, ca sa sari direct la ce vrei sa schimbi
export const Steps = ({labels, step, done, onPick}: {labels: string[], step: number, done?: boolean, onPick?: (index: number) => void}) => (
	<ol className='vtm-steps' aria-label='Pașii formularului'>
		{labels.map((label, index) => {
			const isDone = done || index < step;
			const content = <><i className={isDone || index === step ? '' : 'is-off'}>{isDone ? '✓' : index + 1}</i><span className='vtm-steps-t'>{label}</span></>;
			return (
				<li key={label} aria-current={index === step ? 'step' : undefined} className={index === step ? 'is-on' : ''}>
					{onPick ? <button type='button' onClick={() => onPick(index)}>{content}</button> : content}
				</li>
			);
		})}
	</ol>
);

export const SuccessIcon = () => (
	<div className='vtm-success-icon'><IconCheck/></div>
);

export const Loading = () => <div className='vtm-empty vtm-meta'>Se încarcă…</div>;

// ---------- Contact direct ----------
// Butoanele apar doar daca omul a bifat afisarea numarului; numarul vine de la server doar utilizatorilor logati.
// type = FORMING | AD | PLAYER | NEW_PLAYER ; uuid = uuid-ul tinta pentru acel tip
// Numarul intreg (doar fondatorul/managerul il primeste de la backend); pe mobil deschide apelul
export const PhoneLink = ({phone}: {phone?: string | null}) => {
	if (!phone) return null;
	return <a href={`tel:${phone.replace(/\s/g, '')}`} className='vtm-meta' style={{fontWeight: 800, color: 'inherit'}}>{phone}</a>;
};

// Anunt „caut echipa” (tab-ul Jucatori cauta echipa): transfer = legitimat la o echipa aprobata, altfel jucator liber
// Contul aprobat din Administrare utilizatori se invita ca orice jucator cu cont (pe email, cu link de raspuns);
// inscrierea veche de pe site n-are cont: invitatie doar in aplicatie
export const newPlayerTarget = (player: any) => {
	const meta = [player.age > 0 && `${player.age} ani`, player.position, 'jucător nou'].filter(Boolean).join(' · ');
	return player.account
		? {targetType: 'USER', targetUuid: player.uuid, name: player.name, meta, status: 'recent', position: player.position}
		: {targetType: 'NEW_PLAYER', targetUuid: player.uuid, name: player.name, meta, status: 'new', position: player.position};
};

export const seekingTarget = (player: any, name: string) => ({
	targetType: 'USER', targetUuid: player.userUuid, name, position: player.position,
	meta: `${player.age ?? '–'} ani · ${player.position} · ${player.transfer ? `acum la ${player.currentTeam}` : 'fără echipă'}`,
	status: player.transfer ? 'transfer' : 'seeking', transfer: player.transfer,
});

export const ContactButtons = ({contact, waOnly, outline, withText, withIcons, btnClass = ''}: any) => {
	const {open} = useTm();
	if (!contact?.hasPhone) return null;
	const role = contact?.role ? ` ${contact.role}` : '';
	const iconCls = withText ? '' : ' vtm-btn--icon';
	const waCls = outline ? 'vtm-btn--s vtm-btn--wa-outline' : 'vtm-btn--wa';
	return (
		<>
			<button type='button' className={`vtm-btn ${waCls}${iconCls} ${btnClass}`.trim()}
					aria-label={`Scrie pe WhatsApp${role}`}
					onClick={() => open('contact', {...contact, channel: 'whatsapp'})}>
				{withText ? <>{withIcons && <IconWhatsApp/>}WhatsApp</> : <IconWhatsApp/>}
			</button>
			{!waOnly && !outline && (
				<button type='button' className={`vtm-btn vtm-btn--s${iconCls} ${btnClass}`.trim()}
						aria-label={`Sună${role}`}
						onClick={() => open('contact', {...contact, channel: 'call'})}>
					{withText ? <>{withIcons && <IconPhone/>}Sună</> : <IconPhone/>}
				</button>
			)}
		</>
	);
};
